/**
 * 登录状态。
 *
 * 单一真相是 **client.ts 的 localStorage**，不是这个 store：SSE 与 client.ts
 * 都要在 React 之外读令牌，把令牌的所有权放进 zustand 会逼出一条
 * 「模块级变量镜像 store」的旁路，两份状态必然漂移。这里只做 UI 镜像。
 *
 * 因此本 store **不持久化**：刷新页面后 `bootstrap()` 会拿 localStorage 里的
 * 令牌去问一次 `/auth/status`，由服务端裁定它还作不作数。持久化用户信息只会
 * 让「令牌早就失效了但界面还显示着头像」这种状态多存在几百毫秒。
 */

import { create } from 'zustand';
import * as authApi from '@/api/auth';
import { getApiKey, getAuthToken, setAuthToken } from '@/api/client';
import type { AuthStatus, UserInfo } from '@/types/api';

/** 未初始化 → 正在探测 → 结论。渲染守卫必须区分「还不知道」和「确定没登录」。 */
export type AuthPhase = 'idle' | 'checking' | 'ready';

interface AuthState {
  phase: AuthPhase;
  /** 服务端是否要求身份。false = 本地开发模式，不挡任何人 */
  authRequired: boolean;
  /** 一个账号都还没有，应引导创建初始管理员 */
  needsBootstrap: boolean;
  user: UserInfo | null;

  /** 应用启动时调一次。失败（后端没起）不抛，交给页面显示离线态。 */
  bootstrap: () => Promise<void>;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** 401 时由 client.ts 的全局回调触发：清本地状态，不发请求 */
  clearSession: () => void;
  createFirstAdmin: (username: string, password: string) => Promise<void>;
}

function applyStatus(s: AuthStatus): Partial<AuthState> {
  return {
    phase: 'ready',
    authRequired: s.auth_required,
    needsBootstrap: s.needs_bootstrap,
    user: s.user,
  };
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  phase: 'idle',
  authRequired: false,
  needsBootstrap: false,
  user: null,

  bootstrap: async () => {
    if (get().phase === 'checking') return;
    set({ phase: 'checking' });
    try {
      set(applyStatus(await authApi.getAuthStatus()));
    } catch {
      // 后端不可达时**保守地认为需要登录**：反过来（认为不需要）会让用户以为
      // 自己已进入系统，然后每个业务请求各弹一次错。
      set({ phase: 'ready', authRequired: true, needsBootstrap: false, user: null });
    }
  },

  signIn: async (username, password) => {
    const res = await authApi.login({ username, password });
    // 先写令牌再置 user：中间任何一次渲染都不该出现「有用户但没令牌」的状态，
    // 那会让守卫放行、随后每个请求 401。
    setAuthToken(res.token);
    set({ phase: 'ready', authRequired: true, needsBootstrap: false, user: res.user });
  },

  signOut: async () => {
    try {
      await authApi.logout();
    } catch {
      // 令牌可能已过期/已被踢。服务端删不掉不影响本地登出——本地这一步才是
      // 用户真正要的，不能因为一次网络失败就把人留在登录态里。
    }
    setAuthToken('');
    set({ user: null });
  },

  clearSession: () => {
    setAuthToken('');
    set({ user: null, authRequired: true });
  },

  createFirstAdmin: async (username, password) => {
    await authApi.bootstrapAdmin({ username, password, role: 'ADMIN' });
    await get().signIn(username, password);
  },
}));

/** 当前是否已具备访问业务页面的身份。 */
export function hasAccess(s: Pick<AuthState, 'authRequired' | 'user'>): boolean {
  if (!s.authRequired) return true; // 本地开发模式，服务端不挡
  if (s.user) return true;
  // 机器身份：设置页填了 API Key、没有登录账号。放行，让服务端去判定。
  // 不放行的话「用 API Key 调试控制台」这条既有用法会被登录改造掐断。
  return !getAuthToken() && Boolean(getApiKey());
}

/** 收到 401 之后该把人送去哪。 */
export type UnauthorizedAction = 'login' | 'api-key';

/**
 * 判据是「**刚才发出去的是哪种凭证**」，不是「现在 store 里有没有 user」。
 *
 * 曾经按 user 判，而这恰好在最常见的那个场景下判反：会话过期时
 * `/auth/status` 认不出令牌，于是 user 是 null —— 走进"没有登录用户"分支，
 * 弹出一个 **API Key 输入框**。用户手上只有账号口令、根本没有 Key，
 * 于是卡在一个填不了的对话框前，而正确动作就是回登录页重登一次。
 *
 * 凭证优先级见 `client.ts#authHeaders`：会话令牌优先。所以只有在
 * **没有会话令牌、只有 API Key** 时，401 才真的是"Key 不对"。
 */
export function unauthorizedAction(): UnauthorizedAction {
  if (!getAuthToken() && getApiKey()) return 'api-key';
  return 'login';
}
